/* oxlint-disable react/no-this-in-sfc */
import format from "date-fns/format";
import Highcharts from "highcharts";
import { HighchartsReactRefObject } from "highcharts-react-official";
import { useMemo, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import { useAppVariant } from "@jugl-web/utils";
import { WidgetTooltip } from "../WidgetTooltip";
import { ImpactDistributionDataEntry } from "./types";

export const useImpactDistributionChart = (
  data: ImpactDistributionDataEntry[]
) => {
  const chartRef = useRef<HighchartsReactRefObject>(null);
  const { isMobile } = useAppVariant();

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        type: "bar",
        spacing: [0, 0, 0, 0],
        style: {
          fontFamily: "Poppins",
        },
      },
      title: {
        text: undefined,
      },
      accessibility: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      legend: {
        enabled: false,
      },
      xAxis: {
        categories: data.map((d) => d.impactType),
        reversed: true,
        gridLineWidth: 0,
        lineWidth: 0,
        labels: {
          align: "right",
          style: {
            color: "#1A2023",
            fontSize: isMobile ? "11px" : "14px",
            fontWeight: "500",
            width: isMobile ? "80px" : undefined,
          },
        },
        title: {
          text: undefined,
        },
      },
      yAxis: {
        min: 0,
        max: 100,
        tickInterval: 5,
        gridLineWidth: 1,
        gridLineColor: "#DEDEE4",
        gridLineDashStyle: "Dash",
        lineWidth: 0,
        labels: {
          format: "{value}%",
          style: {
            color: "#5A6367",
            fontSize: "12px",
          },
        },
        title: {
          text: undefined,
        },
      },
      tooltip: {
        useHTML: true,
        followPointer: false,
        followTouchMove: false,
        backgroundColor: "transparent",
        borderWidth: 0,
        shadow: false,
        padding: 0,
        style: { whiteSpace: "normal" },
        formatter() {
          const point = this as Highcharts.Point & {
            custom?: { chatsCount: number };
          };

          const subtitle = format(new Date(), "MMMM, yyyy");

          const chatsCount = point.custom?.chatsCount ?? 0;

          return ReactDOMServer.renderToStaticMarkup(
            <WidgetTooltip
              title={point.category?.toString() ?? ""}
              subtitle={subtitle}
              content={
                <div className="flex flex-col gap-2">
                  <div>
                    <span className="text-base font-semibold text-dark">
                      {point.y ?? 0}%{" "}
                    </span>{" "}
                    <span className="font-sm font-normal text-dark-900">
                      impact share
                    </span>
                  </div>
                  <div>
                    <span className="text-base font-semibold text-dark">
                      {chatsCount}
                    </span>{" "}
                    <span className="font-sm font-normal text-dark-900">
                      {chatsCount === 1 ? "session" : "sessions"}
                    </span>
                  </div>
                </div>
              }
            />
          );
        },
      },
      plotOptions: {
        series: {
          animation: false,
        },
        bar: {
          pointWidth: 20,
          minPointLength: 10,
          borderWidth: 0,
          borderRadius: "50%",
          groupPadding: 0,
          dataLabels: {
            enabled: true,
            x: 5,
            style: {
              color: "#1A2023",
              fontSize: "12px",
              fontWeight: "500",
            },
            formatter() {
              const y = typeof this.y === "number" ? this.y : 0;
              return `${Math.round(y)}%`;
            },
          },
        },
      },
      series: [
        {
          type: "bar",
          data: data.map((item) => ({
            y: item.percentage,
            color: item.color,
            custom: { chatsCount: item.chatsCount },
          })),
        },
      ],
    }),
    [data, isMobile]
  );

  const isEmpty = data.length === 0;

  return {
    chartRef,
    options,
    isEmpty,
  };
};
